"use client"
import { Box, Button, Flex, HStack, Icon, Link, Text } from "@chakra-ui/react"
import { usePathname } from "next/navigation"
import {
  type MouseEvent,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react"

import Logo from "@/components/Logo/Logo"
import NextLink from "@/components/RouteTransitionLink"
import SearchModal from "@/components/SearchModal/SearchModal"
import { ColorModeButton } from "@/components/ui"
import { MailLuIcon, SearchOutlineIcon } from "@/components/icons"

interface NavItem {
  href: `/${string}`
  label: string
}

type NavigationClickHandler = (event: MouseEvent<HTMLAnchorElement>) => void

const NAV_ITEMS: NavItem[] = [
  { href: "/work", label: "Work" },
  {
    href: "/writing",
    label: "Writing",
  },
]

const NAV_SCROLL_DELTA = 4

interface NavbarUiState {
  navHidden: boolean
  navHiddenPathname: string | null
  searchOpen: boolean
}

type NavbarUiAction =
  | { type: "set-nav-hidden"; hidden: boolean; pathname: string | null }
  | { type: "set-search-open"; open: boolean }
  | { type: "open-search"; pathname: string | null }

function navbarUiReducer(
  state: NavbarUiState,
  action: NavbarUiAction
): NavbarUiState {
  switch (action.type) {
    case "open-search":
      return {
        ...state,
        navHidden: false,
        navHiddenPathname: action.pathname,
        searchOpen: true,
      }
    case "set-nav-hidden":
      return {
        ...state,
        navHidden: action.hidden,
        navHiddenPathname: action.pathname,
      }
    case "set-search-open":
      return { ...state, searchOpen: action.open }
    default:
      return state
  }
}

const LONDON_TIME_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  hour12: false,
  minute: "2-digit",
  timeZone: "Europe/London",
  timeZoneName: "short",
})

function formatLondonTime() {
  return LONDON_TIME_FORMATTER.format(new Date())
}

function isActivePath(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`)
}

function shouldBlurAfterNavigationClick(event: MouseEvent<HTMLAnchorElement>) {
  if (
    event.detail === 0 ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return false
  }

  const target = event.currentTarget.getAttribute("target")
  if (target && target !== "_self") return false
  if (event.currentTarget.hasAttribute("download")) return false

  const currentUrl = new URL(window.location.href)
  const nextUrl = new URL(event.currentTarget.href)

  return (
    currentUrl.pathname !== nextUrl.pathname ||
    currentUrl.search !== nextUrl.search
  )
}

function AvailabilityStrip() {
  const [clock, setClock] = useState<string | null>(null)

  useEffect(() => {
    const initialTimeout = window.setTimeout(() => {
      setClock(formatLondonTime())
    }, 0)

    const interval = window.setInterval(() => {
      setClock(formatLondonTime())
    }, 1000)

    return () => {
      window.clearTimeout(initialTimeout)
      window.clearInterval(interval)
    }
  }, [])

  return (
    <Box
      bg="bg.subtle/65"
      borderBottomColor="border"
      borderBottomWidth="1px"
      className="site-availability-strip"
      width="full"
    >
      <Flex
        align="center"
        color="fg.muted"
        fontFamily="mono"
        fontSize="11px"
        justify="space-between"
        letterSpacing="0"
        maxW="1440px"
        mx="auto"
        px={{ base: 4, md: 6, lg: 6, "2xl": 4 }}
        py={2.5}
        textTransform="uppercase"
      >
        <HStack gap={3} minW={0}>
          <Box
            aria-hidden="true"
            bg={{ base: "green.500", _dark: "#d4ff3a" }}
            borderRadius="full"
            boxSize="6px"
            css={{
              "--pulse-shadow-color": {
                base: "rgba(71, 255, 58, 0.5)",
                _dark: "rgba(212, 255, 58, 0.5)",
              },
              animation: "greenPulse 2.4s infinite",
            }}
            flexShrink={0}
          />
          <Text as="span" truncate>
            Available · Remote UK / EU
          </Text>
        </HStack>

        <HStack
          display={{ base: "none", md: "flex" }}
          fontVariantNumeric="tabular-nums"
          gap={6}
        >
          <Text as="span">53.4807° N · 2.2426° W</Text>
          <Text as="span" minW="8ch">
            {clock ?? "--:--"}
          </Text>
        </HStack>
      </Flex>
    </Box>
  )
}

function SearchButton({ onOpen }: { onOpen: () => void }) {
  return (
    <Button
      _hover={{
        bg: "bg.emphasized",
        color: "fg",
        textDecoration: "none",
      }}
      alignItems="center"
      aria-label="Open search"
      borderRadius="full"
      color="currentColor"
      display={{ base: "inline-flex", sm: "inline-flex" }}
      fontSize="13px"
      gap={2}
      h="36px"
      justifyContent="center"
      minW="36px"
      onClick={onOpen}
      px={{ base: 2 }}
      py={"-1px"}
      textDecoration="none"
      transition="color 180ms ease, background-color 180ms ease"
      variant="ghost"
    >
      <Icon
        _focus={{ outline: "none" }}
        aria-hidden={true}
        as={SearchOutlineIcon}
        boxSize="18px"
        focusable={false}
        pointerEvents="none"
      />
    </Button>
  )
}

interface NavTextLinkProps extends NavItem {
  onNavigateClick: NavigationClickHandler
  pathname: string | null
}

function NavTextLink({
  href,
  label,
  onNavigateClick,
  pathname,
}: NavTextLinkProps) {
  const active = pathname ? isActivePath(pathname, href) : false

  return (
    <Link
      _focusVisible={{
        outline: "1px solid {colors.primary.400}",
        outlineOffset: "8px",
      }}
      _hover={{
        backgroundSize: "100% 1px",
        color: "fg",
        textDecoration: "none",
      }}
      asChild
      backgroundImage="linear-gradient(currentColor, currentColor)"
      backgroundPosition="0 100%"
      backgroundRepeat="no-repeat"
      backgroundSize={active ? "100% 1px" : "0% 1px"}
      color={active ? "fg" : "fg.muted"}
      display="inline-flex"
      fontSize="13px"
      lineHeight="1"
      pb="2px"
      textDecoration="none"
      textWrap="nowrap"
      transition="color 180ms ease, background-size 300ms cubic-bezier(.2,.7,.2,1)"
      whiteSpace="nowrap"
      outlineColor="primary.400"
    >
      <NextLink
        href={href}
        onClick={onNavigateClick}
        transitionTypes={["nav-forward"]}
      >
        {label}
      </NextLink>
    </Link>
  )
}

function ContactLink({
  active = false,
  onNavigateClick,
}: {
  active?: boolean
  onNavigateClick: NavigationClickHandler
}) {
  return (
    <Link
      _hover={{
        bgColor: "navItem.cta_bg_hover",
        color: "navItem.cta_contrast",
        textDecoration: "none",
        transform: "scale(1.05)",
      }}
      alignItems="center"
      aria-label="Contact"
      asChild
      bg="navItem.cta_bg"
      borderRadius="full"
      color="navItem.cta_contrast"
      display="inline-flex"
      flexShrink={0}
      fontSize="14px"
      gap={{ base: "0px", lg: "8px" }}
      position="relative"
      px={{ base: "12px", lg: "16px" }}
      py={{ base: "8px", md: "8px" }}
      textDecoration="none"
      transition="color 180ms ease, background-color 180ms ease, transform 180ms ease"
    >
      <NextLink
        href="/contact"
        onClick={onNavigateClick}
        transitionTypes={["nav-forward"]}
      >
        <Box
          aria-hidden="true"
          as="span"
          display="inline-flex"
          pointerEvents="none"
          position="relative"
          zIndex={1}
        >
          <MailLuIcon
            aria-hidden="true"
            focusable={false}
            pointerEvents="none"
            boxSize="16px"
          />
        </Box>
        <Text
          as="span"
          backgroundImage="linear-gradient(currentColor, currentColor)"
          backgroundPosition="0 100%"
          backgroundRepeat="no-repeat"
          backgroundSize={active ? "100% 1px" : "0% 1px"}
          className="nav-item-text"
          display={{ base: "none", lg: "block" }}
          lineHeight="1"
          position="relative"
          py="2px"
          transition="color 180ms ease, background-size 300ms cubic-bezier(.2,.7,.2,1)"
          whiteSpace="nowrap"
          zIndex={1}
        >
          Get in touch
        </Text>
      </NextLink>
    </Link>
  )
}

export default function Navbar2() {
  const pathname = usePathname()
  const [uiState, dispatchUiState] = useReducer(navbarUiReducer, {
    navHidden: false,
    navHiddenPathname: pathname,
    searchOpen: false,
  })
  const navHidden = uiState.navHiddenPathname === pathname && uiState.navHidden
  const blurAfterNavigationRef = useRef(false)
  const headerRef = useRef<HTMLDivElement | null>(null)
  const hideThresholdRef = useRef(0)
  const lastScrollYRef = useRef(0)
  const animationFrameRef = useRef<number | null>(null)

  const setCurrentNavHidden = useCallback(
    (hidden: boolean) => {
      dispatchUiState({ hidden, pathname, type: "set-nav-hidden" })
    },
    [pathname]
  )

  const setSearchOpen = useCallback((open: boolean) => {
    dispatchUiState({ open, type: "set-search-open" })
  }, [])

  const handleNavigationClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      blurAfterNavigationRef.current = shouldBlurAfterNavigationClick(event)
    },
    []
  )

  const handleSearchOpen = useCallback(() => {
    dispatchUiState({ pathname, type: "open-search" })
  }, [pathname])

  useEffect(() => {
    if (!blurAfterNavigationRef.current) return

    blurAfterNavigationRef.current = false

    const activeElement = document.activeElement
    if (
      activeElement instanceof HTMLElement &&
      activeElement.closest("[data-primary-nav]")
    ) {
      activeElement.blur()
    }
  }, [pathname])

  useEffect(() => {
    const commitNavHidden = (hidden: boolean) => {
      dispatchUiState({ hidden, pathname, type: "set-nav-hidden" })
    }

    const updateHideThreshold = () => {
      const header = headerRef.current

      if (!header) {
        hideThresholdRef.current = 0
        return
      }

      hideThresholdRef.current = header.offsetHeight
    }

    updateHideThreshold()
    lastScrollYRef.current = window.scrollY

    const updateNavbarVisibility = () => {
      const currentScrollY = Math.max(window.scrollY, 0)
      const previousScrollY = lastScrollYRef.current
      const scrollDelta = Math.abs(currentScrollY - previousScrollY)

      if (currentScrollY <= hideThresholdRef.current) {
        commitNavHidden(false)
      } else if (scrollDelta >= NAV_SCROLL_DELTA) {
        commitNavHidden(currentScrollY > previousScrollY)
      }

      lastScrollYRef.current = currentScrollY
      animationFrameRef.current = null
    }

    const handleScroll = () => {
      if (animationFrameRef.current !== null) return
      animationFrameRef.current = window.requestAnimationFrame(
        updateNavbarVisibility
      )
    }

    const handleResize = () => {
      updateHideThreshold()

      if (window.scrollY <= hideThresholdRef.current) {
        commitNavHidden(false)
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("scroll", handleScroll)
      window.removeEventListener("resize", handleResize)

      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [pathname])

  return (
    <>
      <AvailabilityStrip />
      <SearchModal open={uiState.searchOpen} setOpen={setSearchOpen} />
      <Box
        as="header"
        bg="bg.paper/85"
        borderBottom={"1px solid {colors.border}"}
        className="site-header"
        css={{ backdropFilter: "blur(12px)" }}
        onFocusCapture={() => setCurrentNavHidden(false)}
        position="sticky"
        ref={headerRef}
        top="-1px"
        transform={navHidden ? "translateY(-100%)" : "translateY(0)"}
        transition="transform 240ms ease, background-color 180ms ease, border-color 180ms ease"
        width="100%"
        willChange="transform"
        zIndex={50}
      >
        <Flex
          align="center"
          h={16}
          justify="space-between"
          maxW="1440px"
          mx="auto"
          px={{ base: 4, md: 6, lg: 6, "2xl": 4 }}
        >
          <HStack gap={3} minW={0}>
            <Logo
              props={{
                _hover: { color: "fg.muted", textDecoration: "none" },
                flexShrink: 0,
              }}
              size={5}
            />
            <Text
              as="span"
              color="fg.muted"
              display={{ base: "none", lg: "inline" }}
              fontFamily="mono"
              fontSize="12px"
              letterSpacing="0"
              lineHeight="1"
              textTransform="uppercase"
              whiteSpace="nowrap"
            >
              | Senior Product Designer
            </Text>
            <ColorModeButton
              _focus={{ outlineColor: "primary" }}
              _hover={{ bg: "bg.emphasized", color: "fg" }}
              aria-label="Toggle theme"
              borderRadius="full"
              color="currentColor"
              p={2}
              size="xs"
              variant="ghost"
            />{" "}
          </HStack>

          <HStack
            aria-label="Primary navigation"
            as="nav"
            color="fg.muted"
            data-primary-nav=""
            gap={{ base: 4, md: 4, lg: 7 }}
            minW={0}
          >
            {NAV_ITEMS.map((item) => (
              <NavTextLink
                key={item.href}
                {...item}
                onNavigateClick={handleNavigationClick}
                pathname={pathname}
              />
            ))}
            <SearchButton onOpen={handleSearchOpen} />
            <ContactLink
              active={pathname ? isActivePath(pathname, "/contact") : false}
              onNavigateClick={handleNavigationClick}
            />
          </HStack>
        </Flex>
      </Box>
    </>
  )
}
